import { Async, Actions, FSpread } from '@client/new/helpers/types';
import { useUserStore } from '@client/new/stores/user.store';
import { useState, useMemo, useEffect } from 'react';
import toast from 'react-hot-toast';
import { PATHS } from '@client/routes';
import { PortalUser } from '@playbook-technologies/common-enums';
import { navigate } from '@client/new/helpers/history';
import { useGuideModel, Checklist, Strategy } from './model';
import auth from '@client/components/auth';
import {
  useClearSelectedStrategyMutation,
  useSaveOnboardingDismissalMutation,
  Platform,
} from 'generated/graphql';
import { trackEvent, usePublishedProducts } from '@services/tracking';

export type GuideVM = {
  dispatch: GuideVMDispatcher;
  strategy: Strategy | null;
  checklists: Checklist[];
  activeChecklist: Checklist | null;
  completedChecklistsCount: number;
  totalChecklistsCount: number;
  totalEstimatedTime: string;
  influencerId: number;
  isLive: boolean;
  canGoLive: boolean;
  openingChecklistId: string | null;
  isCollapsed: boolean;
  goLiveModal: {
    isOpen: boolean;
    onClose: () => void;
    goLive: () => void;
    inProgress: boolean;
  };
  taskModal: {
    templateId: string | null;
    isOpen: boolean;
    onClose: () => void;
  };
};

export type PayloadMap = {
  toggleTask: { taskId: string; completed: boolean };
  openChecklist: string;
  collapseActiveChecklist: null;
  dismiss: null;
  openGoLiveModal: null;
  closeGoLiveModal: null;
  goLive: null;
  reviewStrategy: null;
  openTaskModal: string;
  closeTaskModal: null;
};

export type GuideVMActions = Actions<PayloadMap>;
export type GuideVMDispatcher = FSpread<PayloadMap>;

const getSelectedStrategyId = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  const value = params.get('strategyId');
  if (!value || value.trim() === '') return null;
  return value;
};

export const useGuideViewModel = (p: {
  influencerId?: string;
}): Async<GuideVM> => {
  const { user } = useUserStore();
  const influencerId = p.influencerId
    ? Number(p.influencerId)
    : user.influencerId;
  const selectedStrategyId = getSelectedStrategyId();

  const [goLiveModalOpen, setGoLiveModalOpen] = useState(false);
  const [openingChecklistId, setOpeningChecklistId] = useState<string | null>(
    null,
  );
  const [isDismissing, setIsDismissing] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [optimisticActiveId, setOptimisticActiveId] = useState<string | null>(
    null,
  );
  const [taskModalTemplateId, setTaskModalTemplateId] = useState<string | null>(
    null,
  );

  const creatorIdStr = String(influencerId ?? 0);
  const publishedProducts = usePublishedProducts({
    influencerId: creatorIdStr,
  });
  const [clearStrategyMutation] = useClearSelectedStrategyMutation();
  const [saveDismissalMutation] = useSaveOnboardingDismissalMutation();

  // Pass 0 as fallback when influencerId is undefined - model handles this with skip
  const model = useGuideModel(influencerId ?? 0, selectedStrategyId);

  const checklistsWithOptimisticActive = useMemo(() => {
    if (model.type !== 'fetched') return [];
    if (!optimisticActiveId) return model.value.checklists;

    return model.value.checklists.map((c) => ({
      ...c,
      isActive: c.id === optimisticActiveId,
    }));
  }, [model, optimisticActiveId]);

  const activeChecklist = useMemo(() => {
    if (model.type !== 'fetched') return null;
    return checklistsWithOptimisticActive.find((c) => c.isActive) ?? null;
  }, [model, checklistsWithOptimisticActive]);

  useEffect(() => {
    if (model.type !== 'fetched' || activeChecklist) return;

    const firstIncomplete = model.value.checklists.find((c) =>
      c.tasks.some((t) => !t.isChecked),
    );
    const checklistToActivate = firstIncomplete ?? model.value.checklists[0];

    if (checklistToActivate) {
      model.value.setActiveChecklist(checklistToActivate.id);
    }
  }, [model, activeChecklist]);

  const completedChecklistsCount = useMemo(() => {
    if (model.type !== 'fetched') return 0;
    return model.value.checklists.filter((c) =>
      c.tasks.every((t) => t.isChecked),
    ).length;
  }, [model]);

  const totalEstimatedTime = useMemo(() => {
    if (model.type !== 'fetched') return '-';
    return model.value.strategy?.launchTimeLabel ?? '-';
  }, [model]);

  const canGoLive = useMemo(() => {
    if (model.type !== 'fetched') return false;
    const { onboardingStatus } = model.value;
    return (
      onboardingStatus.profileReady &&
      onboardingStatus.paymentMethodReady &&
      onboardingStatus.collectionPublishedReady
    );
  }, [model]);

  if (model.type !== 'fetched') return model;
  if (publishedProducts.type !== 'fetched') return publishedProducts;

  const dispatch: GuideVMDispatcher = (type, ...args) => {
    const action = { type, payload: args[0] } as GuideVMActions;

    switch (action.type) {
      case 'toggleTask': {
        const task = model.value.checklists
          .flatMap((c) => c.tasks)
          .find((t) => t.id === action.payload.taskId);

        if (action.payload.completed) {
          trackEvent({
            type: 'Todo Checked',
            payload: { 'Todo Title': task?.title ?? '' },
          });
        }

        model.value
          .toggleTask(action.payload)
          .then(() => {
            const currentChecklist = checklistsWithOptimisticActive.find(
              (c) => c.isActive,
            );
            if (!currentChecklist) return;

            const isNowCompleted = currentChecklist.tasks.every((t) => {
              if (t.id === action.payload.taskId)
                return action.payload.completed;
              return t.isChecked;
            });

            if (isNowCompleted) {
              trackEvent({ type: 'Mark as Complete' });

              const nextIncomplete = checklistsWithOptimisticActive.find(
                (c) => !c.isActive && c.tasks.some((t) => !t.isChecked),
              );

              if (nextIncomplete) {
                setIsCollapsed(true);
                setTimeout(() => {
                  dispatch('openChecklist', nextIncomplete.id);
                }, 300);
              }
            }
          })
          .catch((err) => {
            toast.error(err.message);
          });
        break;
      }

      case 'openChecklist': {
        const previousChecklist = activeChecklist;
        const nextChecklist = model.value.checklists.find(
          (c) => c.id === action.payload,
        );

        trackEvent({
          type: 'Next Goal / Previous Goal',
          payload: {
            'Current goal title': nextChecklist?.title ?? '',
            'Previous goal title': previousChecklist?.title ?? '',
          },
        });

        setIsCollapsed(false);
        setOptimisticActiveId(action.payload);
        model.value
          .setActiveChecklist(action.payload)
          .then(() => {
            setOptimisticActiveId((current) =>
              current === action.payload ? null : current,
            );
          })
          .catch((err) => {
            setOptimisticActiveId((current) =>
              current === action.payload ? null : current,
            );
            toast.error(err.message);
          });
        break;
      }

      case 'collapseActiveChecklist':
        setIsCollapsed((prev) => !prev);
        break;

      case 'dismiss': {
        const homePath = auth.isInfluencer()
          ? PATHS.HOME
          : PATHS.HOME_CREATOR(influencerId);

        setIsDismissing(true);
        Promise.all([
          clearStrategyMutation({ variables: { creatorId: creatorIdStr } }),
          saveDismissalMutation({
            variables: { creatorId: creatorIdStr, platform: Platform.Web },
          }),
        ])
          .then(() => {
            navigate(homePath);
          })
          .catch((err) => {
            toast.error(err.message || 'Failed to dismiss guide');
          })
          .finally(() => {
            setIsDismissing(false);
          });
        break;
      }

      case 'openGoLiveModal':
        trackEvent({
          type: 'Go Live Tapped',
          payload: {
            'Product Type': publishedProducts.value.publishedProductType,
            'Collection Type': publishedProducts.value.publishedCollectionType,
          },
        });
        setGoLiveModalOpen(true);
        break;

      case 'closeGoLiveModal':
        setGoLiveModalOpen(false);
        break;

      case 'goLive': {
        const goLiveHomePath = auth.isInfluencer()
          ? PATHS.HOME
          : PATHS.HOME_CREATOR(influencerId);
        model.value
          .goLive()
          .then(() => {
            trackEvent({
              type: 'Creator Is Live',
              payload: {
                'Product Type': publishedProducts.value.publishedProductType,
                'Collection Type':
                  publishedProducts.value.publishedCollectionType,
              },
            });

            setGoLiveModalOpen(false);
            toast.success('Congratulations! You are now live!');
            navigate(goLiveHomePath);
          })
          .catch((err) => {
            toast.error(err.message);
          });
        break;
      }

      case 'reviewStrategy': {
        trackEvent({
          type: 'Strategy Reviewed',
          payload: { 'Current strategy': model.value.strategy?.name ?? '' },
        });

        const isInfluencer = user?.role === PortalUser.Role.Influencer.value;
        const onboardingPath =
          !isInfluencer && influencerId
            ? `${PATHS.ONBOARDING_ADMIN(influencerId)}?step=strategy`
            : `${PATHS.ONBOARDING}?step=strategy`;
        navigate(onboardingPath);
        break;
      }

      case 'openTaskModal':
        if (action.payload === 'checklist-go-live' && model.value.isLive) {
          break;
        }
        setTaskModalTemplateId(action.payload);
        break;

      case 'closeTaskModal':
        setTaskModalTemplateId(null);
        break;
    }

    return null;
  };

  return {
    type: 'fetched',
    value: {
      dispatch,
      strategy: model.value.strategy,
      checklists: checklistsWithOptimisticActive,
      activeChecklist,
      completedChecklistsCount,
      totalChecklistsCount: checklistsWithOptimisticActive.length,
      totalEstimatedTime,
      influencerId: model.value.influencerId,
      isLive: model.value.isLive,
      canGoLive,
      openingChecklistId,
      isCollapsed,
      goLiveModal: {
        isOpen: goLiveModalOpen,
        onClose: () => dispatch('closeGoLiveModal'),
        goLive: () => dispatch('goLive'),
        inProgress: model.value.goLiveInProgress,
      },
      taskModal: {
        templateId: taskModalTemplateId,
        isOpen: taskModalTemplateId !== null,
        onClose: () => dispatch('closeTaskModal'),
      },
    },
  };
};
